import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material'
import { 
  Google, 
  Visibility, 
  VisibilityOff, 
  Person, 
  Lock,
  Gavel,
} from '@mui/icons-material'
import { signInWithPopup } from 'firebase/auth'
import { auth, googleProvider } from '../config/firebase'
import { API_BASE_URL } from '../config/api'
import { useAppDispatch } from '../hooks/redux'
import { useLoginMutation, useGoogleLoginMutation } from '../store/api/authApi'
import { setCredentials, setUser } from '../store/slices/authSlice'
import { gradientBackdrop, gradientBrand } from '../theme/brand'

export default function LoginPage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const [login, { isLoading, error }] = useLoginMutation()
  const [googleLogin, { isLoading: isGoogleLoading }] = useGoogleLoginMutation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [googleError, setGoogleError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setGoogleError(null)
    try {
      const result = await login({ username, password }).unwrap()
      // Set token first
      dispatch(setCredentials({ token: result.access_token, user: null }))
      
      // Fetch user info using RTK Query
      try {
        const userResponse = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${result.access_token}`
          }
        })
        if (userResponse.ok) {
          const userData = await userResponse.json()
          dispatch(setUser(userData))
        }
      } catch (err) {
        console.error('Failed to fetch user info:', err)
      }
      
      navigate('/')
    } catch (err) {
      console.error('Login failed:', err)
    }
  }

  const handleGoogleLogin = async () => {
    setGoogleError(null)
    try {
      // signInWithPopup sẽ mở một popup window riêng biệt (không phải tab mới)
      // Popup window sẽ hiển thị Google login form
      // Sau khi user đăng nhập thành công, popup sẽ tự động đóng
      // Note: Warning về COOP policy không ảnh hưởng đến chức năng popup
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user
      
      // Get ID token từ Firebase Auth
      const idToken = await user.getIdToken()
      
      // Gửi ID token lên backend để lấy JWT token của hệ thống
      const response = await googleLogin({ id_token: idToken }).unwrap()
      
      // Lưu credentials vào Redux store và localStorage
      dispatch(setCredentials({ token: response.access_token, user: response.user }))
      dispatch(setUser(response.user))
      
      // Redirect về trang chủ sau khi login thành công
      navigate('/')
    } catch (err: any) {
      console.error('Google login failed:', err)
      if (err.code === 'auth/popup-closed-by-user') {
        setGoogleError('Đăng nhập bị hủy. Vui lòng thử lại.')
      } else if (err.code === 'auth/popup-blocked') {
        setGoogleError('Popup bị chặn bởi trình duyệt. Vui lòng cho phép popup cho trang web này và thử lại.')
      } else if (err.code === 'auth/cancelled-popup-request') {
        setGoogleError('Đăng nhập bị hủy. Vui lòng thử lại.')
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setGoogleError('Tài khoản này đã được đăng ký với phương thức khác.')
      } else {
        setGoogleError(`Đăng nhập Google thất bại: ${err.message || 'Vui lòng thử lại.'}`)
      }
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradientBackdrop,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '800px',
          height: '800px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      }}
    >
      <Container 
        maxWidth="sm" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          width: '100%',
          py: 4,
        }}
      >
        <Card
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}
        >
          {/* Header với gradient */}
          <Box
            sx={{
              background: gradientBrand,
              color: 'white',
              p: 4,
              textAlign: 'center',
            }}
          >
            <Gavel 
              sx={{ 
                fontSize: 64, 
                mb: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
              }} 
            />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              E-Court Platform
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Hệ thống quản lý tranh tụng trực tuyến
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight="600" align="center" gutterBottom sx={{ mb: 3 }}>
              Đăng nhập
            </Typography>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.
              </Alert>
            )}

            {/* Google Error - Temporarily hidden */}
            {false && googleError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                }}
              >
                {googleError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Tên đăng nhập"
                margin="normal"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <TextField
                fullWidth
                label="Mật khẩu"
                type={showPassword ? 'text' : 'password'}
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading || isGoogleLoading}
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  background: gradientBrand,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  boxShadow: '0 4px 18px rgba(20, 78, 140, 0.35)',
                  '&:hover': {
                    background: gradientBrand,
                    filter: 'brightness(1.06)',
                    boxShadow: '0 6px 22px rgba(246, 65, 131, 0.35)',
                  },
                  '&:disabled': {
                    background: 'rgba(0,0,0,0.12)',
                    filter: 'none',
                  },
                }}
              >
                {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
              </Button>
            </Box>

            {/* Google Login - Temporarily hidden */}
            {false && (
              <>
                <Divider sx={{ my: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                    HOẶC
                  </Typography>
                </Divider>

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<Google />}
                  onClick={handleGoogleLogin}
                  disabled={isLoading || isGoogleLoading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: '#db4437',
                    color: '#db4437',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#c23321',
                      backgroundColor: 'rgba(219, 68, 55, 0.04)',
                      borderWidth: 2,
                    },
                    '&:disabled': {
                      borderColor: 'rgba(0,0,0,0.12)',
                      color: 'rgba(0,0,0,0.26)',
                    },
                  }}
                >
                  {isGoogleLoading ? 'Đang đăng nhập...' : 'Đăng nhập với Google'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Footer text */}
        <Typography 
          variant="body2" 
          align="center" 
          sx={{ 
            mt: 3, 
            color: 'white',
            opacity: 0.9,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          © 2026 E-Court Platform · UEL / N2NAI. Tất cả quyền được bảo lưu.
        </Typography>
      </Container>

      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
    </Box>
  )
}
